import { emptyArray } from 'ui-components/default-props/empty';

import { STRING_TO_ENUM_MAPPING, TodoId } from './todo-id-mapping';

/**
 * @typedef {object} NormalizedTodo
 * @property {number} id - The todo ID as enum value
 * @property {boolean} isVisible - Whether the todo card should be visible/rendered
 * @property {boolean} isCompleted - Whether the todo is completed on home page
 */

/**
 * Check if a numeric ID is a valid TodoId enum value
 * @param {number} id - The numeric ID to check
 * @returns {boolean} True if the ID exists in the TodoId enum
 */
export const isValidTodoId = (id) => {
  return Object.values(TodoId).includes(id);
};

/**
 * Convert string ID to enum ID or pass through enum ID
 * @param {string|number} id - The todo ID to normalize
 * @returns {number|null} The normalized enum ID, or null if unknown
 */
export const normalizeTodoId = (id) => {
  if (typeof id === 'string') {
    const enumId = STRING_TO_ENUM_MAPPING[id];
    if (enumId === undefined) {
      return null;
    }
    return enumId;
  }

  // For numeric IDs, check if they're valid TodoId enum values
  if (typeof id === 'number' && !isValidTodoId(id)) {
    return null;
  }

  return id;
};

/**
 * Check if a string is in the new compact format ("id-isVisible-isCompleted")
 * @param {string} str - The string to check
 * @returns {boolean} True if it matches the compact format
 */
export const isCompactFormat = (str) => {
  if (typeof str !== 'string') return false;
  const parts = str.split('-');
  if (parts.length !== 3) return false;
  // Check if all parts are valid non-negative integers
  return parts.every((part) => /^\d+$/.test(part));
};

/**
 * Convert compact format string to normalized todo object
 * @param {string} compactStr - The compact format string ("id-isVisible-isCompleted")
 * @returns {NormalizedTodo|null} The normalized todo object, or null if invalid
 */
export const parseCompactFormat = (compactStr) => {
  if (!isCompactFormat(compactStr)) {
    return null;
  }

  const [idStr, visibleStr, completedStr] = compactStr.split('-');
  const id = parseInt(idStr, 10);

  // Validate that the ID is a known TodoId
  if (!isValidTodoId(id)) {
    return null;
  }

  const isVisible = visibleStr === '1';
  const isCompleted = completedStr === '1';

  return {
    id,
    isVisible,
    isCompleted,
  };
};

/**
 * Convert normalized todo object to compact format string for storage
 * @param {NormalizedTodo} todo - The normalized todo object
 * @returns {string} The compact format string ("id-isVisible-isCompleted")
 */
export const toCompactFormat = (todo) => {
  const { id, isVisible, isCompleted } = todo;
  const visibleInt = isVisible ? 1 : 0;
  const completedInt = isCompleted ? 1 : 0;
  return `${id}-${visibleInt}-${completedInt}`;
};

/**
 * Normalize a todo object to the new format
 * @param {string | object} todo - The todo to normalize
 * @returns {NormalizedTodo|null} The normalized todo object, or null if unknown ID
 */
export const normalizeTodoObject = (todo) => {
  // Handle new compact format: "id-isVisible-isCompleted"
  if (typeof todo === 'string') {
    if (isCompactFormat(todo)) {
      const parsed = parseCompactFormat(todo);
      // Safety: If compact format parsing fails, fall back to string ID logic
      if (parsed !== null) {
        return parsed;
      }
    }

    // Handle legacy string ID format (includes fallback for invalid compact format)
    const normalizedId = normalizeTodoId(todo);
    if (normalizedId === null) {
      return null;
    }
    return {
      id: normalizedId,
      isVisible: true,
      isCompleted: false,
    };
  }

  // Handle object formats

  if (todo.id == null) {
    return null;
  }

  const normalizedId = normalizeTodoId(todo.id);
  if (normalizedId === null) {
    return null;
  }

  return {
    id: normalizedId,
    isVisible: todo.isVisible ?? todo.visible ?? todo.checked ?? true,
    isCompleted: todo.isCompleted ?? todo.isComplete ?? false,
  };
};

/**
 * Normalize an array of todos
 * @param {Array<string | object>} todos - Array of todos to normalize
 * @returns {Array<NormalizedTodo>} Array of normalized todos (filtered to remove null values)
 */
export const normalizeTodosArray = (todos = emptyArray) => (todos || emptyArray).map(normalizeTodoObject).filter((todo) => todo !== null);

/**
 * Check if a todo should be visible (for rendering cards)
 * @param {NormalizedTodo} todo - The todo to check
 * @returns {boolean} True if todo should be visible
 */
export const isTodoVisible = (todo) => todo.isVisible;

/**
 * Filter todos to only visible ones (for card rendering)
 * @param {Array<NormalizedTodo>} todos - Array of todos to filter
 * @returns {Array<NormalizedTodo>} Array of visible todos
 */
export const getVisibleTodos = (todos = emptyArray) => todos.filter(isTodoVisible);

/**
 * Convert any todo format to compact format for storage
 * @param {Array<string | object>} todos - Array of todos in any format
 * @returns {Array<string>} Array of compact format strings
 */
export const convertTodosForStorage = (todos = emptyArray) => {
  const normalized = normalizeTodosArray(todos);
  return normalized.map(toCompactFormat);
};

/**
 * Filter todos to only incomplete ones (for home page list)
 * @param {Array<NormalizedTodo>} todos - Array of todos to filter
 * @returns {Array<NormalizedTodo>} Array of incomplete todos
 */
export const getIncompleteTodos = (todos = emptyArray) => todos.filter((todo) => !todo.isCompleted);

/**
 * Default todos that should appear for all users who have started the Setup Wizard
 * These are automatically added if missing when the FTU wizard context is loaded
 * Format: "id-isVisible-isCompleted" (compact format)
 * @type {Array<string>} Array of default todos in compact format
 */
export const DEFAULT_TODOS_COMPACT = [
  '21-1-0', // GET_STARTED_WEBINAR - visible, not completed
  '22-1-0', // ACCOUNT_OPTIMIZATION_WEBINAR - visible, not completed
  '23-1-0', // DEMO_ORDER - visible, not completed
  '8-1-0', // BRANDED_TRACKING - visible, not completed
  '25-1-0', // UPLOAD_PRODUCTS - visible, not completed
  '26-1-0', // ACCOUNT_USERS - visible, not completed
  '4-1-0', // CONNECT (ShipStation Connect) - visible, not completed
  '32-1-0', // SETUP_INSURANCE - visible, not completed
  '33-1-0', // PHONE_NUMBER_CAPTURE - visible, not completed
];

/**
 * Ensure all default todos are present in the provided todos array
 * Adds missing default todos in compact format with visible=true, completed=false
 * @param {Array<string | object>} existingTodos - Current todos in any format
 * @returns {Array<string>} Updated todos array in compact format with all defaults included
 */
export const ensureDefaultTodosPresent = (existingTodos = emptyArray) => {
  // Convert existing todos to compact format
  const existingCompact = convertTodosForStorage(existingTodos);

  // Parse existing todos to get their IDs
  const normalizedExisting = normalizeTodosArray(existingTodos);
  const existingIds = new Set(normalizedExisting.map((todo) => todo.id));

  // Find missing default todos by checking compact format IDs
  const missingDefaults = DEFAULT_TODOS_COMPACT.filter((compactTodo) => {
    const parsed = parseCompactFormat(compactTodo);
    return parsed && !existingIds.has(parsed.id);
  });

  // Combine existing and missing todos (all in compact format)
  return [...existingCompact, ...missingDefaults];
};
